/**
 * Halloween phone wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenPhoneWallpapersPack: ToilePack = {
    slug: 'halloween-phone-wallpapers',
    category: 'season',
    name: 'Halloween phone wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'moon', label: 'Pumpkin moon', subject: 'A carved pumpkin glowing at the bottom of the frame under a huge full moon, a bare tree, a calm sky above'},
        {key: 'house', label: 'Haunted house', subject: 'A haunted house on a hill with lit windows and bats rising into a calm night sky'},
        {key: 'cat', label: 'Black cat', subject: 'A black cat sitting on a fence with an arched back, a moon behind, a calm dark sky above'},
    ],
    styles: [
        {key: 'cute', label: 'Cute', style: 'cute flat illustration, rounded shapes, soft and quiet', palette: 'Pastel purple, orange and cream', framing: 'The focal point in the lower third and the upper half calm for the clock and icons'},
        {key: 'dark', label: 'Dark', style: 'atmospheric digital painting, deep night, fog, a faint glow', palette: 'Black, deep purple and orange', framing: 'The focal point low, the sky calm above for widgets'},
        {key: 'minimal', label: 'Minimal', style: 'minimal flat illustration, few shapes, lots of calm ground', palette: 'Black and orange on cream', framing: 'A quiet center for icons, the motif at the edges'},
    ],
    directions: ["a witch's hat instead", 'in bright candy colors', 'for a tablet screen'],
    directionHint: "try: a witch's hat instead",
    seo: {
        title: 'Halloween phone wallpapers: pumpkin, house, cat',
        metaDescription: 'Nine Halloween lock screen wallpapers — a pumpkin under the moon, a haunted house, a black cat — in cute, dark or minimal style, calm top for icons.',
        h1: 'Halloween phone wallpaper: pumpkin, haunted house, black cat',
        tagline: 'Nine wallpapers, three October scenes, three styles each — the top stays calm so the clock and icons sit clean.',
        intro: "A lock screen has to leave room for the clock — the top can't be busy. These nine set a carved pumpkin under a full moon, a haunted house with rising bats, and a black cat on a fence, each as cute, dark or minimal. Ask for a different scene, like a witch's hat, and the same calm top carries it.",
        useCases: [
            'an iPhone or Android lock screen',
            'a spooky October phone background',
            'a calm background behind widgets and the clock',
            'a matching wallpaper across phone and tablet',
        ],
        faq: [
            {q: 'Will the clock and icons stay readable over the wallpaper?', a: 'Yes — every wallpaper keeps its top calm and low-contrast, so the clock, notifications and app icons stay legible over it.'},
            {q: 'What size do these render at?', a: 'Portrait phone proportions, composed so the focal point sits low and the top third stays quiet.'},
            {q: "Can I request a different scene, like a witch's hat?", a: 'Yes — the direction field takes any October scene, and the same three styles render it with a calm top.'},
            {q: "What's the difference between cute, dark and minimal?", a: 'Cute is a soft rounded flat illustration, dark is an atmospheric night painting with fog, and minimal is a flat few-shape illustration.'},
        ],
    },
}
